import React, { useEffect, useState } from 'react';
import { Loader2, PlayCircle } from 'lucide-react';
import { api } from '../../hooks/useApi';
import { videoIdOf } from '../../lib/videos';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const CHECK_EVERY_MS = 5000;

const Message = ({ busy, children }) => (
  <div className="aspect-video flex items-center justify-center gap-2 bg-[#0A0A0A] rounded-md text-sm text-[#94A3B8] p-6 text-center" data-testid="video-message">
    {busy && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
    <span>{children}</span>
  </div>
);

// Plays one video in a pop-up. While Bunny is still preparing it, checks back
// every few seconds and starts on its own when it is ready.
const VideoDialog = ({ file, onClose }) => {
  const [video, setVideo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let stopped = false;
    let timer = null;
    const check = async () => {
      try {
        const v = await api.get(`/sales/videos/${videoIdOf(file.url)}`);
        if (stopped) return;
        setVideo(v);
        if (v.state === 'processing') timer = setTimeout(check, CHECK_EVERY_MS);
      } catch (e) {
        if (!stopped) setError(e?.response?.data?.detail || 'Could not load the video');
      }
    };
    check();
    return () => { stopped = true; clearTimeout(timer); };
  }, [file.url]);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-4xl bg-[#121212] border-[#1E293B] text-[#F8FAFC]"
        onClick={(e) => e.stopPropagation()}
        data-testid="video-player-dialog"
      >
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{file.name || 'Video'}</DialogTitle>
          <DialogDescription className="sr-only">Video player</DialogDescription>
        </DialogHeader>
        {error && <Message>{error}</Message>}
        {!error && !video && <Message busy>Loading…</Message>}
        {!error && video?.state === 'processing' && (
          <Message busy>This video is still being prepared. It will start here when it's ready.</Message>
        )}
        {!error && video?.state === 'failed' && (
          <Message>This video could not be processed. Remove it and upload it again.</Message>
        )}
        {!error && video?.state === 'ready' && (
          <iframe
            src={video.embed_url}
            title={file.name || 'Video player'}
            className="block w-full aspect-video rounded-md border-0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            data-testid="video-player-frame"
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// A video's name on an activity. Clicking it opens the player.
export const VideoLink = ({ file, className = '', iconClassName = 'w-4 h-4 shrink-0', testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`text-left ${className}`}
        data-testid={testId}
      >
        <PlayCircle className={iconClassName} />
        <span className="truncate">{file.name || 'Video'}</span>
      </button>
      {open && <VideoDialog file={file} onClose={() => setOpen(false)} />}
    </>
  );
};

export default VideoLink;
